import { UserRole } from '@taxassist/api-client';

/**
 * Credit management permission utilities
 */

/**
 * Check if user can add credit batches to firms
 * Only ADMIN users can add credits
 */
export const canAddCredits = (userRole?: string): boolean => {
  return userRole === UserRole.admin;
};

/**
 * Check if user can view credit batches and details
 * All roles except firm_user can view credit batches
 */
export const canViewCreditBatches = (userRole?: string): boolean => {
  return userRole !== UserRole.firm_user;
};

/**
 * Check if user can view credit history/audit trail
 * ADMIN, REVIEWER, and FIRM_ADMIN can view credit history
 */
export const canViewCreditHistory = (userRole?: string): boolean => {
  const allowedRoles: string[] = [UserRole.admin, UserRole.reviewer, UserRole.firm_admin];
  return userRole ? allowedRoles.includes(userRole) : false;
};

/**
 * Check if user can view basic credit information (total credits, expiry warnings)
 * All authenticated users can see basic credit info
 */
export const canViewBasicCreditInfo = (userRole?: string): boolean => {
  return !!userRole;
};

/**
 * Get user-friendly permission explanation for credit features
 */
export const getCreditPermissionExplanation = (feature: 'add' | 'view' | 'history', userRole?: string): string => {
  switch (feature) {
    case 'add':
      return canAddCredits(userRole) 
        ? 'You can add new credit batches to firms'
        : 'Only system administrators can add credit batches';
    
    case 'view':
      return canViewCreditBatches(userRole)
        ? 'You can view detailed credit batch information'
        : 'Credit batch details are not available to your role';
    
    case 'history':
      return canViewCreditHistory(userRole)
        ? 'You can view credit transaction history'
        : 'Credit history is only available to administrators and firm admins';
    
    default:
      return 'Permission information not available';
  }
};